import { useCallback, useEffect, useState } from "react";

/** The HTTP status of a failed request, if the server answered at all. */
export function httpStatusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/**
 * "forbidden" is its own state: a person without the right is not an error,
 * and the page says so instead of showing a failure.
 */
export type LoadState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "forbidden" }
  | { status: "failed" };

/**
 * Loads once, and again whenever `reload` is called. `request` must keep the
 * same identity between renders (wrap it in useCallback), or it loads on
 * every render.
 */
export function useLoadedSetting<T>(request: () => Promise<T>) {
  const [state, setState] = useState<LoadState<T>>({ status: "loading" });

  const fetchState = useCallback(async (): Promise<LoadState<T>> => {
    try {
      return { status: "ready", data: await request() };
    } catch (err) {
      return { status: httpStatusOf(err) === 403 ? "forbidden" : "failed" };
    }
  }, [request]);

  useEffect(() => {
    // An answer that arrives after the request changed or the page closed is dropped.
    let wanted = true;
    fetchState().then((next) => {
      if (wanted) setState(next);
    });
    return () => {
      wanted = false;
    };
  }, [fetchState]);

  const reload = async () => setState(await fetchState());

  return { state, reload };
}
